import type BackOfficeConnector from "../data/backOfficeConnector";
import type ChangeVehicleRegistrationRequest from "../entities/changeVehicleRegistrationRequest";
import type ChangeEmailRequest from "../entities/changeEmailRequest";
import type CustomerPartialRefundCandidate from "../entities/customerPartialRefundCandidateRequest";
import type CustomerRefundCandidate from "../entities/customerRefundCandidateRequest";
import type LicensePlate from "../entities/licensePlate";
import type EventBus from "../services/eventBus";
import { serializeFilters } from "../utils/filters";
import RepositoryBase from "./repositoryBase";
import ChargeResponse from "../entities/chargeResponse";
import ChargeChangesDuringValidityItemResponse from "../entities/chargeChangesDuringValidityItemResponse";
import GetSingleChargeDetailResponse from "../entities/getSingleChargeDetailResponse";
import type { IPagination } from "../data/types";
import type VehicleChangeSearchRequest from "../entities/vehicleChangeSearchRequest";
import ChargeRefundResponse from "../entities/chargeRefundResponse";
import type { AxiosResponse } from "axios";
import CustomerRefundCandidatePreviewResponse from "entities/customerRefundCandidatePreviewResponse";
import CustomerPartialRefundCandidatePreviewResponse from "entities/customerPartialRefundCandidatePreviewResponse";
import type CustomerRefundCandidatePreviewTransferRefundPaymentResponse from "../entities/customerRefundCandidatePreviewTransferRefundPaymentResponse";
import ChargePriceListsResponse from "entities/chargePriceListsResponse";
import type SearchChargesRequest from "entities/searchChargesRequest";
import ChargesTotalCountResponse from "entities/chargesTotalCountResponse";
import type GetChargesTotalCountRequest from "../entities/getChargesTotalCountRequest";
import ChargeChange from "entities/chargeChange";
import type FuelTypeChangeRequest from "../entities/fuelTypeChangeRequest";
import FuelTypeChangePreviewResponse from "../entities/fuelTypeChangePreviewResponse";
import { ChargeVehicleChangeRequest } from "entities/chargeVehicleChangeRequest";
import VehicleChangePreviewResponse from "entities/vehicleChangePreviewResponse";
import VehicleRegistrationChangePreviewResponse from "../entities/vehicleRegistrationChangePreviewResponse";

export default class ChargesRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getAll(filters: SearchChargesRequest, pagination: IPagination, abortSignal?: AbortSignal) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("charge_search")
          .postPagedEntities<ChargeResponse>({ ...serializeFilters(filters, false), ...pagination }, ChargeResponse, abortSignal),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  getCharge(filters: SearchChargesRequest, chargeId: string) {
    filters.chargeId = chargeId;
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("charge_search")
          .postPagedEntities<ChargeResponse>({ ...serializeFilters(filters, false) }, ChargeResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  getOne(chargeId: string) {
    return this.callApi(api => api.all("call_center").one("charges", chargeId).getEntity(GetSingleChargeDetailResponse), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  getAllChanges(charge: ChargeResponse) {
    return this.callApi(api => api.all("call_center").one("charges", charge.chargeId).all("changes").getEntities(ChargeChange), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  totalCount(entity: GetChargesTotalCountRequest) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("charge_search")
          .all("total_count")
          .postEntity<ChargesTotalCountResponse>({ ...serializeFilters(entity, false) }, ChargesTotalCountResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  changePlate(chargeVehicle: ChargeVehicleChangeRequest, chargeId: string, isDisabledEmailChange = false) {
    const request = new ChargeVehicleChangeRequest();

    request.licensePlate = chargeVehicle.licensePlate;
    request.officialRecordId = chargeVehicle.officialRecordId;
    request.countryId = chargeVehicle.countryId;
    if (isDisabledEmailChange === false) {
      request.emailAddress = chargeVehicle.emailAddress;
    }

    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("charges", chargeId)
          .all("vehicle")
          .putOnly({
            ...serializeFilters(request, false),
          }),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  changePlatePreview(chargeVehicle: ChargeVehicleChangeRequest, chargeId: string, isDisabledEmailChange = false) {
    const request = new ChargeVehicleChangeRequest();

    request.licensePlate = chargeVehicle.licensePlate;
    request.officialRecordId = chargeVehicle.officialRecordId;
    request.countryId = chargeVehicle.countryId;
    if (isDisabledEmailChange === false) {
      request.emailAddress = chargeVehicle.emailAddress;
    }

    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("charges", chargeId)
          .all("vehicle_change_preview")
          .postEntity({ ...serializeFilters(request, false) }, VehicleChangePreviewResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  changeAllPlates(vehicle: LicensePlate, payload: ChangeVehicleRegistrationRequest) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("vehicle_registrations")
          .all(vehicle.countryId)
          .all(vehicle.licensePlate)
          .put(serializeFilters(payload, false)),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  getRefunds(chargeId: string) {
    return this.callApi(api => api.all("call_center").one("charges", chargeId).all("refunds").getEntities(ChargeRefundResponse), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
    });
  }

  refundPreview(chargeId: string, payload: CustomerRefundCandidate) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("charges", chargeId)
          .all("refund_candidate_preview")
          .postEntity(payload, CustomerRefundCandidatePreviewResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  refund(chargeId: string, payload: CustomerRefundCandidate | CustomerRefundCandidatePreviewTransferRefundPaymentResponse) {
    return this.callApi(
      api => api.all("call_center").one("charges", chargeId).all("refund_candidates").postWithoutResponse(payload),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  fuelTypeChangeRefundPreview(chargeId: string, payload: CustomerPartialRefundCandidate) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("charges", chargeId)
          .all("partial_refund_candidate_preview")
          .postEntity(payload, CustomerPartialRefundCandidatePreviewResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  fuelTypeChangePreview(chargeId: string, payload: FuelTypeChangeRequest) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("charges", chargeId)
          .all("fuel_type_change_preview")
          .postEntity(serializeFilters(payload, false), FuelTypeChangePreviewResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  fuelTypeChangeRefund(
    chargeId: string,
    payload: CustomerPartialRefundCandidate | CustomerRefundCandidatePreviewTransferRefundPaymentResponse
  ) {
    return this.callApi(
      api => api.all(`call_center/charges/${chargeId}/partial_refund_candidates`).postOnly(serializeFilters(payload, false)),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  fuelTypeUpdate(chargeId: string, payload: FuelTypeChangeRequest | CustomerRefundCandidatePreviewTransferRefundPaymentResponse) {
    return this.callApi(api => api.all(`call_center/charges/${chargeId}/fuel_type`).putOnly(serializeFilters(payload, false)), {
      ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
    });
  }

  sendPaymentConfirmation(charge: ChargeResponse, forceSingleChargeReceipt: boolean) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("charge_orders", charge.chargeOrderId)
          .all("payment_confirmation_email")
          .postOnly({ chargeId: charge.chargeId, forceSingleChargeReceipt: forceSingleChargeReceipt }),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
      }
    );
  }

  changeEmail(chargeId: string, changeEmailRequest: ChangeEmailRequest) {
    return this.callApi(
      api => api.all("call_center").one("charges", chargeId).all("email").putEntity(changeEmailRequest, ChargeResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
      }
    );
  }

  changesDuringValidity(chargeId: string) {
    return this.callApi(api =>
      api
        .all("call_center")
        .one("charges", chargeId)
        .all("changes_during_validity")
        .getEntities(ChargeChangesDuringValidityItemResponse)
    );
  }
  vehicleRegistrationChangeSearch(request: VehicleChangeSearchRequest, countryId: string, licensePlate: string) {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("vehicle_registrations")
          .one(countryId)
          .one(licensePlate)
          .all("change_preview")
          .postEntity(serializeFilters(request, false), VehicleRegistrationChangePreviewResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  async downloadAnonymizedReceipt(chargeId: string): Promise<AxiosResponse<File>> {
    return this.callApi(api => api.all("call_center").one("charges", chargeId).all("anonymized_receipt").getRaw() as any, {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
    });
  }

  /** Price lists used for the simple mapping of `priceListItemId` throughout the application. */
  chargePriceLists() {
    return this.callApi(
      api =>
        api
          .all("simple_lists")
          .all("price_lists")
          .getEntity(ChargePriceListsResponse, serializeFilters({ include_invalid: true })),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  /** Price lists used by the charge types administration module. */
  chargeTypePriceLists() {
    return this.callApi(
      api =>
        api
          .all("call_center")
          .all("charges")
          .all("price_lists")
          .getEntity(ChargePriceListsResponse, serializeFilters({ include_invalid: true })),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }
}
